import EmailIcon from '@mui/icons-material/Email';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Inline, Stack } from '@/ui';
import { useBreadcrumbs } from '@/ui/layout/breadcrumbsContext';
import { PageBreadcrumbs } from '@/ui/layout/PageBreadcrumbs';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import { Box, Link, Text } from '@/ui/primitives';
import { s } from '../../theme/tokens';
import type { SxProps, Theme } from '@/ui/primitives';
import type { PropsWithChildren, ReactElement, ReactNode } from 'react';

type PublicShellProps = PropsWithChildren<{
  background?: 'default' | 'surface';
  title?: ReactNode;
  actions?: ReactNode;
  pinFooterToViewport?: boolean;
}>;

export const PublicShell = ({
  children,
  background = 'default',
  title = 'FlowQA UI',
  actions,
  pinFooterToViewport = true,
}: PublicShellProps): ReactElement => {
  const layoutMode = useLayoutMode();
  const { items: breadcrumbItems, visible: breadcrumbsVisible } = useBreadcrumbs();
  const showBreadcrumbs = breadcrumbsVisible && breadcrumbItems.length > 0;
  const shellPaddingX = layoutMode === layoutModeTokens.wide ? s('lg') : s('md');
  const headerPaddingX = s('md');
  const breadcrumbPaddingX = layoutMode === layoutModeTokens.wide ? s('md') : s('sm');
  const footerLinkSx = {
    color: 'text.secondary',
    textDecorationColor: 'currentColor',
    '&:hover': {
      color: 'text.secondary',
      textDecorationColor: 'currentColor',
    },
    '& .MuiTypography-root': {
      color: 'inherit',
    },
  } as const;
  const footerTextSx = {
    lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
  } satisfies SxProps<Theme>;
  const [scrollReady, setScrollReady] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setScrollReady(true);
    }, 150);
    return () => {
      window.clearTimeout(id);
    };
  }, []);

  return (
    <Box
      sx={{
        height: '100dvh',
        minHeight: '100dvh',
        backgroundColor: background === 'surface' ? 'background.paper' : 'background.default',
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        borderTop: 'none',
        overflow: 'hidden',
      }}
    >
      <a className="flow-skip-link" href="#main-content">
        Skip to main content
      </a>
      <Box
        component="header"
        sx={(theme) => ({
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: theme.app.colors.surfaceAlt,
          paddingTop: s('sm'),
          paddingBottom: s('xs'),
          boxSizing: 'border-box',
          width: '100%',
          transform: 'translateZ(0)',
          willChange: 'transform',
          display: 'flex',
          alignItems: 'center',
          gap: s('md'),
        })}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: s('md'),
            minWidth: 0,
            width: '100%',
            px: headerPaddingX,
            boxSizing: 'border-box',
          }}
        >
          <Text role="sectionTitle">{title}</Text>
          <Box sx={{ marginLeft: 'auto' }}>{actions}</Box>
        </Box>
      </Box>
      <Box
        component="main"
        id="main-content"
        sx={{
          minHeight: 0,
          overflowY: scrollReady ? 'auto' : 'hidden',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {showBreadcrumbs ? (
          <Box
            sx={(theme) => ({
              borderBottom: `1px solid ${theme.app.colors.border}`,
              paddingX: breadcrumbPaddingX,
              paddingTop: s('lg'),
              paddingBottom: s('md'),
            })}
          >
            <PageBreadcrumbs />
          </Box>
        ) : null}
        <Box sx={{ flex: '1 0 auto' }}>{children}</Box>
        <Box
          component="footer"
          sx={(theme) => ({
            backgroundColor: theme.app.colors.surfaceAlt,
            paddingX: shellPaddingX,
            paddingY: s('lg'),
            mt: pinFooterToViewport ? 'auto' : s('none'),
          })}
        >
          <Stack align="center" justify="center" gap="sm">
            <Stack gap="xs" alignItems="center" sx={{ width: 'auto' }}>
              <Inline gap="md" align="center" justify="center" wrap="nowrap" sx={{ width: 'auto' }}>
                <Link
                  component={RouterLink}
                  to="/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={footerLinkSx}
                >
                  <Text role="body" component="span" sx={footerTextSx}>
                    Terms of Service
                  </Text>
                </Link>
                <Text role="body" color="textSecondary" sx={footerTextSx}>
                  ·
                </Text>
                <Link
                  component={RouterLink}
                  to="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={footerLinkSx}
                >
                  <Text role="body" component="span" sx={footerTextSx}>
                    Privacy Policy
                  </Text>
                </Link>
                <Text role="body" color="textSecondary" sx={footerTextSx}>
                  ·
                </Text>
                <Link
                  component={RouterLink}
                  to="/cookie-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={footerLinkSx}
                >
                  <Text role="body" component="span" sx={footerTextSx}>
                    Cookie Policy
                  </Text>
                </Link>
                <Text
                  role="body"
                  color="textSecondary"
                  sx={[footerTextSx, { display: { xs: 'none', sm: 'block' } }]}
                >
                  ·
                </Text>
                <Link
                  component={RouterLink}
                  to="/acceptable-use-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={[footerLinkSx, { display: { xs: 'none', sm: 'inline' } }]}
                >
                  <Text role="body" component="span" sx={footerTextSx}>
                    Acceptable Use Policy
                  </Text>
                </Link>
                <Text
                  role="body"
                  color="textSecondary"
                  sx={[footerTextSx, { display: { xs: 'none', sm: 'block' } }]}
                >
                  ·
                </Text>
                <Link
                  href="#"
                  className="termly-display-preferences"
                  underline="hover"
                  sx={[footerLinkSx, { display: { xs: 'none', sm: 'inline' } }]}
                >
                  <Text role="body" component="span" sx={footerTextSx}>
                    Consent Preferences
                  </Text>
                </Link>
              </Inline>
              <Inline gap="md" align="center" justify="center" wrap="nowrap" sx={{ width: 'auto' }}>
                <Link
                  component={RouterLink}
                  to="/cookie-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={[footerLinkSx, { display: { xs: 'inline', sm: 'none' } }]}
                >
                  <Text role="body" component="span" sx={footerTextSx}>
                    Cookie Policy
                  </Text>
                </Link>
                <Link
                  component={RouterLink}
                  to="/acceptable-use-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={[footerLinkSx, { display: { xs: 'inline', sm: 'none' } }]}
                >
                  <Text role="body" component="span" sx={footerTextSx}>
                    Acceptable Use Policy
                  </Text>
                </Link>
                <Link
                  href="#"
                  className="termly-display-preferences"
                  underline="hover"
                  sx={[footerLinkSx, { display: { xs: 'inline', sm: 'none' } }]}
                >
                  <Text role="body" component="span" sx={footerTextSx}>
                    Consent Preferences
                  </Text>
                </Link>
              </Inline>
            </Stack>
            <Inline gap="md" align="center" justify="center" wrap="nowrap" sx={{ width: 'auto' }}>
              <Inline gap="sm" align="center" wrap="nowrap" sx={{ width: 'auto' }}>
                <EmailIcon fontSize="small" sx={{ color: 'primary.main' }} />
                <Link href="mailto:support@flowqa.com" underline="hover" sx={footerLinkSx}>
                  <Text role="body" component="span" sx={footerTextSx}>
                    support@flowqa.com
                  </Text>
                </Link>
              </Inline>
            </Inline>
            <Text role="body" color="textSecondary" sx={footerTextSx}>
              © {new Date().getFullYear()} FlowQA Ltd
            </Text>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};
